import React, { useEffect } from 'react';
import axios from 'axios';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GoogleAutocompleteField } from 'Douleutaras/components/Forms/Fields/GoogleAutocompleteField';
import { CustomInput } from 'Douleutaras/components/CustomInput/CustomInput';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import { Gis } from 'Douleutaras/components/Forms/gis.class';

const API_URL = COUNTRY_CONFIG.API_URL[(useLocalization()).countryCode];

interface GeoCheckCategoryResponse {
  county_slug: string;
  is_valid: boolean;
  message: string;
  municipality_slug: string;
}

interface ResultCheckedData {
  job_latitude: string;
  job_longitude: string;
  county_slug: string;
  location_query: string;
  is_location_served_by_us: boolean;
}

interface LocationStepProps {
  proId: string;
  validAddresses: Array<string>;
  categoryId: string;
  postJobSettings: {
    INDIVIDUAL_GEO_CHECK: boolean;
  };
  setValidAddresses(validAddresses: Array<string>): void;
  setUserGis(gis: Gis): void;
  // This callback is primarily used to collect data for subsequent transfer to analytics.
  onResultChecked(data: ResultCheckedData): void;
}

interface LocationStepFormState {
  location: string;
  address: string;
  county: string;
  municipality: string;
  server: {
    location: string;
  };
}

const LocationStep = ({
                        proId,
                        validAddresses,
                        setValidAddresses,
                        categoryId,
                        postJobSettings,
                        setUserGis,
                        onResultChecked
}: LocationStepProps) => {

  const { register, setError, clearErrors, trigger, setValue, watch, formState: { errors } } = useFormContext<LocationStepFormState>();
  const { t } = useTranslation();
  const fieldProps = register('address');

  const doGeoCheck = async geoCheckParams => {
    const geoCheck = await axios.get<GeoCheckCategoryResponse>(`${API_URL}api/public/geo_check_category/`, geoCheckParams);

    const geoCheckResponse = geoCheck.data;

    if (typeof onResultChecked === 'function') {
      onResultChecked({
        job_latitude: geoCheckParams.params.query.latitude,
        job_longitude: geoCheckParams.params.query.longitude,
        county_slug: geoCheckResponse.county_slug,
        location_query: geoCheckParams.params.query.description,
        is_location_served_by_us: geoCheckResponse.is_valid
      });
    }

    if (!geoCheckResponse.is_valid) {
      setError('server.location', {
        type: 'server',
        message: t('T.VALIDATOR.Location is invalid')
      });
    } else {
      if (!validAddresses.includes(geoCheckParams.params.location)) {
        validAddresses.push(geoCheckParams.params.location);
      }
      setValidAddresses(validAddresses);
      clearErrors('server.location');
    }

    setValue('county', geoCheckResponse.county_slug);
    setValue('municipality', geoCheckResponse.municipality_slug);
    setValue('location', geoCheckParams.params.location);
    setUserGis(geoCheckParams.params.query);
    trigger(); // Trigger the form to pass values and trigger validations

    return geoCheckParams.params.query;
  };

  const checkLocation = async (location: Gis) => {
    if (location.longitude === undefined) {
      return;
    }
    const point = `POINT(${location.longitude} ${location.latitude})`;
    const geoCheckParams = {
      params: {
        format: 'json',
        category_id: categoryId,
        query: location,
        location: point,
        pro_id: undefined
      }
    };

    // If a pro exists, add pro_id to the request params
    if (proId && !postJobSettings.INDIVIDUAL_GEO_CHECK) {
      geoCheckParams.params.pro_id = proId;
    }

    return doGeoCheck(geoCheckParams);
  };

  useEffect(() => {
    const currentValues = watch();
    if (currentValues.location !== undefined
        && currentValues.location !== ''
        && currentValues.address !== undefined
        && currentValues.address !== ''
    ) {
      const geoCheckParams = {
        params: {
          format: 'json',
          category_id: categoryId,
          query: currentValues.address,
          location: currentValues.location
        }
      };
      doGeoCheck(geoCheckParams);
    }
  }, []);

  return <>
    <GoogleAutocompleteField input={fieldProps} onChange={checkLocation}>
      <CustomInput
        id="address"
        placeholder={t('T.POST-JOB.PH.Address')}
        name={fieldProps.name}
      />
    </GoogleAutocompleteField>
    <input type="hidden" { ...register('location', {
      validate: val => validAddresses.includes(val)
    })
    } />
    {errors.server?.location &&
      <CustomError>
        {errors.server.location.message}
      </CustomError>
    }
  </>;
};

export default LocationStep;
